import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  TrophyIcon, UserCircleIcon, XMarkIcon, TrashIcon,
  ChartBarIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';

const ROLES = [
  { id: 'all', label: 'All Roles' },
  { id: 'director', label: 'Director' },
  { id: 'admin', label: 'Office Admin' },
  { id: 'teacher', label: 'Full-Time Teacher' },
  { id: 'float', label: 'Part-Time Float' },
  { id: 'cook', label: 'Cook' },
];

const ROLE_LABEL = {
  director: 'Director', admin: 'Office Admin', teacher: 'Teacher', float: 'Float', cook: 'Cook',
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ─── Period generation per template ────────────────────────
// monthly        → last 6 months as { label: "2026-05", short: "May", date: "2026-05-01" }
// review_periods → from template.period_labels JSONB
// annual         → current year, e.g. "2026"
// quarterly      → last 4 quarters, e.g. "2026-Q2"
function periodsForTemplate(t, today = new Date()) {
  if (t.measurement_type === 'review_periods') {
    const labels = Array.isArray(t.period_labels)
      ? t.period_labels
      : (t.period_labels ? JSON.parse(t.period_labels) : []);
    return labels.map((l) => ({ label: l, short: l, date: null }));
  }
  if (t.measurement_type === 'annual' || t.measurement_type === 'one_time') {
    const y = today.getFullYear();
    return [{ label: String(y), short: String(y), date: `${y}-01-01` }];
  }
  if (t.measurement_type === 'quarterly') {
    const out = [];
    let y = today.getFullYear();
    let q = Math.floor(today.getMonth() / 3) + 1;
    for (let i = 0; i < 4; i++) {
      out.unshift({
        label: `${y}-Q${q}`,
        short: `Q${q} ${String(y).slice(2)}`,
        date: `${y}-${String((q - 1) * 3 + 1).padStart(2, '0')}-01`,
      });
      q -= 1;
      if (q === 0) { q = 4; y -= 1; }
    }
    return out;
  }
  // default: monthly — last 6 months ending with current
  const out = [];
  const y0 = today.getFullYear();
  const m0 = today.getMonth();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(y0, m0 - i, 1);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    out.push({
      label: `${yy}-${mm}`,
      short: `${MONTH_NAMES[d.getMonth()]} ${String(yy).slice(2)}`,
      date: `${yy}-${mm}-01`,
    });
  }
  return out;
}

function scoreColor(score) {
  if (score == null) return 'bg-gray-50 text-gray-400 border-gray-200';
  const n = Number(score);
  if (n >= 4.5) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (n >= 3.5) return 'bg-lime-50 text-lime-700 border-lime-200';
  if (n >= 2.5) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-red-50 text-red-700 border-red-200';
}

export default function GoalScorecardsPage() {
  const [staffList, setStaffList] = useState([]);
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [activity, setActivity] = useState(null);
  const [editor, setEditor] = useState(null); // { template, period, existing }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/staff');
        setStaffList(data);
      } catch { toast.error('Failed to load staff'); }
      finally { setLoadingList(false); }
    })();
  }, []);

  const loadMatrix = useCallback(async (id) => {
    if (!id) return;
    setLoadingMatrix(true);
    try {
      const { data } = await api.get(`/goals/staff/${id}/matrix`);
      setMatrix(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load scorecard');
      setMatrix(null);
    } finally { setLoadingMatrix(false); }
  }, []);

  useEffect(() => { loadMatrix(selectedId); }, [selectedId, loadMatrix]);

  // Activity / accountability rollup for the selected staff member.
  useEffect(() => {
    if (!selectedId) { setActivity(null); return; }
    api.get(`/activity/staff/${selectedId}`)
      .then(({ data }) => setActivity(data))
      .catch(() => setActivity(null));
  }, [selectedId]);

  const filteredStaff = useMemo(() => {
    if (roleFilter === 'all') return staffList;
    return staffList.filter((s) => s.role === roleFilter);
  }, [staffList, roleFilter]);

  const selectedStaff = staffList.find((s) => s.id === selectedId);

  const saveScore = async ({ goal_template_id, period_label, period_date, score, notes }) => {
    try {
      await api.post(`/goals/staff/${selectedId}/score`, {
        goal_template_id, period_label, period_date,
        score: score === '' || score == null ? null : Number(score),
        notes: notes || null,
      });
      toast.success(score == null || score === '' ? 'Score cleared' : 'Score saved');
      setEditor(null);
      loadMatrix(selectedId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    }
  };

  return (
    <div className="animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Goal Scorecards</h1>
          <p className="text-gray-500 text-sm">Score staff against their role rubrics. Total avg drives the comp tier.</p>
        </div>
      </div>

      {/* Role filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {ROLES.map((r) => (
          <button key={r.id}
            onClick={() => setRoleFilter(r.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              roleFilter === r.id
                ? 'bg-orange-500 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}>
            {r.label}
          </button>
        ))}
      </div>

      {/* Two-pane layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Left rail: staff list */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden h-fit">
          <div className="px-4 py-3 border-b border-gray-100 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Staff ({filteredStaff.length})
          </div>
          {loadingList ? (
            <div className="p-4 text-sm text-gray-400">Loading…</div>
          ) : filteredStaff.length === 0 ? (
            <div className="p-4 text-sm text-gray-400">No staff in this role.</div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              {filteredStaff.map((s) => (
                <button key={s.id}
                  onClick={() => setSelectedId(s.id)}
                  className={`w-full text-left px-4 py-2.5 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors flex items-center gap-2.5 ${
                    selectedId === s.id ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''
                  }`}>
                  <UserCircleIcon className="w-7 h-7 text-gray-300 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{s.first_name} {s.last_name}</p>
                    <p className="text-xs text-gray-500">{ROLE_LABEL[s.role] || s.role}</p>
                  </div>
                  {selectedId === s.id && <ChevronRightIcon className="w-4 h-4 text-orange-500" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right pane: matrix */}
        <div>
          {!selectedStaff ? (
            <EmptyState />
          ) : loadingMatrix || !matrix ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">
              Loading scorecard…
            </div>
          ) : (
            <ScorecardMatrix
              staff={selectedStaff}
              matrix={matrix}
              activity={activity}
              onCellClick={(template, period, existing) => setEditor({ template, period, existing })}
            />
          )}
        </div>
      </div>

      {/* Score editor modal */}
      {editor && (
        <ScoreEditorModal
          staff={selectedStaff}
          editor={editor}
          onClose={() => setEditor(null)}
          onSave={saveScore}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-14 text-center">
      <TrophyIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
      <p className="text-sm text-gray-500">Pick a staff member to view their goal scorecard.</p>
    </div>
  );
}

function ScorecardMatrix({ staff, matrix, activity, onCellClick }) {
  const { categories, avg_score, tier, total_scored } = matrix;
  const avgDisplay = avg_score == null ? '—' : Number(avg_score).toFixed(2);

  return (
    <div className="space-y-4">
      {/* Staff header card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <UserCircleIcon className="w-12 h-12 text-gray-300" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">{staff.first_name} {staff.last_name}</h2>
              <p className="text-sm text-gray-500">{ROLE_LABEL[staff.role] || staff.role}</p>
            </div>
          </div>
          <div className="flex items-stretch gap-3">
            <div className="bg-gray-50 rounded-lg px-4 py-2 text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Avg Score</p>
              <p className="text-2xl font-bold text-gray-900">{avgDisplay}</p>
              <p className="text-[11px] text-gray-400">{total_scored} scored</p>
            </div>
            {tier ? (
              <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2 text-center min-w-[160px]">
                <p className="text-xs text-orange-600 uppercase tracking-wide">Comp Tier</p>
                <p className="text-2xl font-bold text-orange-700">{Number(tier.raise_percent).toFixed(1)}%</p>
                <p className="text-[11px] text-orange-600">{tier.label}</p>
              </div>
            ) : (
              <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg px-4 py-2 text-center min-w-[160px]">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Comp Tier</p>
                <p className="text-sm text-gray-400 mt-2">No scores yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Accountability — activity-log rollup (who did what) */}
      {activity?.summary?.total > 0 && <AccountabilityCard activity={activity} />}

      {/* Category tables */}
      {categories.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <ChartBarIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            No goal templates configured for this role yet.
          </p>
        </div>
      ) : (
        categories.map((cat) => <CategoryBlock key={cat.name} cat={cat} onCellClick={onCellClick} />)
      )}
    </div>
  );
}

const ACTIVITY_LABELS = {
  'checkin.tuition_override': 'Tuition override', 'checkin.edit_in_time': 'Edited check-in time',
  'checkin.edit_out_time': 'Edited check-out time', 'tuition.late_fee_waive': 'Waived late fee',
  'credit.adjust': 'Adjusted credit', 'family.void': 'Voided family',
  'tuition_plan.create': 'Created tuition plan', 'tuition_plan.update': 'Updated tuition plan',
  'tuition_plan.deactivate': 'Deactivated tuition plan', 'enrollment.create': 'Created enrollment',
  'enrollment.update': 'Updated enrollment', 'enrollment.cancel': 'Cancelled enrollment',
  'enrollment.pause': 'Paused enrollment', 'enrollment.resume': 'Resumed enrollment',
  'document.approve': 'Approved document', 'document.reject': 'Rejected document',
  'document.edit_expiry': 'Edited doc expiry', 'document.reopen': 'Reopened document',
  'disciplinary.create': 'Issued disciplinary',
};
const activityLabel = (a) => ACTIVITY_LABELS[a] || (a?.startsWith('staff_document.') ? `Staff doc ${a.split('.')[1]}` : a);

function AccountabilityCard({ activity }) {
  const s = activity.summary || {};
  const fmt = (ts) => { try { return new Date(ts).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
  const stats = [
    { label: 'Total actions', value: s.total },
    { label: 'Overrides', value: s.overrides, warn: s.overrides > 0 },
    { label: 'Approvals', value: s.approvals },
    { label: 'Edits', value: s.edits },
    { label: 'Waives', value: s.waives },
    { label: 'Voids', value: s.voids, warn: s.voids > 0 },
  ];
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <ChartBarIcon className="w-5 h-5 text-orange-500" />
        <h3 className="text-sm font-bold text-gray-900">Accountability</h3>
        <span className="text-xs text-gray-400">— actions logged across booking + portal</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
        {stats.map((st) => (
          <div key={st.label} className="bg-gray-50 rounded-lg px-3 py-2 text-center">
            <p className={`text-xl font-bold ${st.warn ? 'text-amber-600' : 'text-gray-900'}`}>{st.value ?? 0}</p>
            <p className="text-[11px] text-gray-500">{st.label}</p>
          </div>
        ))}
      </div>
      {activity.recent?.length > 0 && (
        <div className="divide-y border-t pt-1">
          {activity.recent.slice(0, 8).map((ev) => (
            <div key={ev.id} className="py-1.5 flex items-center justify-between text-sm">
              <span className="text-gray-700">
                {activityLabel(ev.action)}
                {ev.target_label && <span className="text-gray-400"> · {ev.target_label}</span>}
                {ev.metadata?.reason && <span className="text-gray-400"> · "{ev.metadata.reason}"</span>}
              </span>
              <span className="text-xs text-gray-400 whitespace-nowrap ml-3">{fmt(ev.occurred_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryBlock({ cat, onCellClick }) {
  // Group templates within this category by their period set so each table aligns.
  const groups = useMemo(() => {
    const map = new Map();
    for (const t of cat.templates) {
      const periods = periodsForTemplate(t);
      const key = periods.map((p) => p.label).join('|');
      if (!map.has(key)) map.set(key, { periods, templates: [] });
      map.get(key).templates.push(t);
    }
    return Array.from(map.values());
  }, [cat]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{cat.name}</h3>
      </div>
      {groups.map((g, gi) => (
        <div key={gi} className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide w-1/3">Goal</th>
                {g.periods.map((p) => (
                  <th key={p.label} className="text-center px-2 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {p.short}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {g.templates.map((t) => (
                <tr key={t.id} className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50">
                  <td className="px-4 py-3 align-top">
                    <p className="font-medium text-gray-900 leading-snug">{t.title}</p>
                    {t.rubric && (
                      <p className="text-xs text-gray-500 mt-1 leading-snug whitespace-pre-line">{t.rubric}</p>
                    )}
                  </td>
                  {g.periods.map((p) => {
                    const existing = (t.scores || []).find((s) => s.period_label === p.label);
                    return (
                      <td key={p.label} className="px-2 py-3 text-center align-middle">
                        <button
                          onClick={() => onCellClick(t, p, existing)}
                          title={existing?.notes || 'Click to score'}
                          className={`inline-flex items-center justify-center w-12 h-9 rounded-md border text-sm font-semibold transition-all hover:scale-105 ${scoreColor(existing?.score)}`}>
                          {existing?.score != null ? Number(existing.score).toString() : '–'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function ScoreEditorModal({ staff, editor, onClose, onSave }) {
  const { template, period, existing } = editor;
  const [score, setScore] = useState(existing?.score != null ? String(existing.score) : '');
  const [notes, setNotes] = useState(existing?.notes || '');
  const max = Number(template.max_score || 5);

  const submit = (e) => {
    e.preventDefault();
    if (score !== '' && (Number(score) < 0 || Number(score) > max)) {
      toast.error(`Score must be 0–${max}`);
      return;
    }
    onSave({
      goal_template_id: template.id,
      period_label: period.label,
      period_date: period.date,
      score, notes,
    });
  };

  const clear = () => {
    onSave({
      goal_template_id: template.id,
      period_label: period.label,
      period_date: period.date,
      score: null, notes: null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
          <div className="pr-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">{period.label}</p>
            <h3 className="text-base font-bold text-gray-900 leading-snug mt-0.5">{template.title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{staff.first_name} {staff.last_name} · {template.category}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {template.rubric && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Rubric</p>
              <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{template.rubric}</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
              Score (0 – {max})
            </label>
            <input type="number" step="0.5" min="0" max={max}
              value={score} onChange={(e) => setScore(e.target.value)}
              autoFocus
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-base focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Notes (optional)</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none resize-none" />
          </div>
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          {existing ? (
            <button type="button" onClick={clear}
              className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700">
              <TrashIcon className="w-4 h-4" /> Clear
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button type="submit"
              className="px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg">Save</button>
          </div>
        </div>
      </form>
    </div>
  );
}
